import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ParticipantResponseStatusesEnum, ParticipantTypesEnum} from '../../../models/api/participantModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {AvatarProps} from './avatar';
import {ContactAvatar} from './contactAvatar';

/*
 * Props.
 */

interface ParticipantAvatarProps extends AvatarProps {
  visualParticipant: VisualParticipantModel;
  isOpen: boolean;
}

/*
 * Styles.
 */

const StyledMeetingRoomDiv = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
`;

interface StyleProps {
  $isOpen: boolean;
  $color: PaletteColorsEnum;
}

export const StyledBadgeDiv = styled.div<StyleProps>`
  position: absolute;
  left: 11.5px;
  top: 10.5px;
  width: 12px;
  height: 12px;
  background-color: ${(p) => p.theme.palette[p.$color].shade40};

  display: flex;
  justify-content: center;
  align-content: center;

  border-radius: 50%;

  ${(p) => boxShadow(p.theme, `0 0 0 1.5px ${p.$isOpen ? p.theme.greys.shade40 : p.theme.greys.white}`)};

  ${(p) => maybeAddBorderHoverStyle(p)}
`;

function maybeAddBorderHoverStyle(props: StyleProps) {
  if (props.$isOpen) {
    return '';
  }

  return css`
    ${hoverSelector} {
      ${(p) => boxShadow(p.theme, `0 0 0 1.5px ${p.theme.greys.shade30}`)};
    }
  `;
}

/*
 * Component.
 */

export const ContactAvatarWithResponseStatus: FC<ParticipantAvatarProps> = (props) => {
  const theme = useUserTheme();
  if (props.visualParticipant.type === ParticipantTypesEnum.RESOURCE) {
    return (
      <StyledMeetingRoomDiv>
        <Icon name="meetingRoomSmall" color={theme.greys.shade70} />
        {maybeRenderBadge(props.visualParticipant.responseStatus, props.isOpen)}
      </StyledMeetingRoomDiv>
    );
  }

  return (
    <ContactAvatar visualContact={props.visualParticipant} {...props}>
      {maybeRenderBadge(props.visualParticipant.responseStatus, props.isOpen)}
    </ContactAvatar>
  );
};

export function maybeRenderBadge(responseStatus: ParticipantResponseStatusesEnum, isOpen: boolean) {
  const color = getColorFromResponseStatus(responseStatus);
  if (!color) {
    return null;
  }

  const styleProps = {
    $isOpen: isOpen,
    $color: color,
  };

  return <StyledBadgeDiv {...styleProps}>{maybeRenderIcon(responseStatus)}</StyledBadgeDiv>;
}

export function maybeRenderIcon(status: ParticipantResponseStatusesEnum) {
  const iconName = getIconNameFromResponseStatus(status);
  if (!iconName) {
    return null;
  }

  return (
    <UserThemeConsumer>{(theme) => <Icon name={iconName} color={theme.greys.white} />}</UserThemeConsumer>
  );
}

export function getColorFromResponseStatus(
  status: ParticipantResponseStatusesEnum,
): PaletteColorsEnum | undefined {
  switch (status) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return PaletteColorsEnum.GREEN;
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return PaletteColorsEnum.ORANGE;
    case ParticipantResponseStatusesEnum.DECLINED:
      return PaletteColorsEnum.RED;
    default:
      return undefined;
  }
}

export function getIconNameFromResponseStatus(status: ParticipantResponseStatusesEnum): IconName | undefined {
  switch (status) {
    case ParticipantResponseStatusesEnum.ACCEPTED:
      return 'checkmarkBoxTiny';
    case ParticipantResponseStatusesEnum.TENTATIVE:
      return 'questionMarkTiny';
    case ParticipantResponseStatusesEnum.DECLINED:
      return 'closeTiny';
    default:
      return undefined;
  }
}
